import type { AccountRole, BillingInterval, LeadRecord, MembershipRecord, PlanCode, PropertyRecord, SessionProfile } from '@growth-rings/fmos-schema';
import { supabase } from './supabase';
import { demoLeads, demoProperties } from './demo-data';

const PROFILE_KEY = 'growth-rings-demo-profile';
const PROPERTY_KEY = 'growth-rings-demo-properties';
const LEAD_KEY = 'growth-rings-demo-leads';

function readLocal<T>(key: string, fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) as T : fallback;
  } catch {
    return fallback;
  }
}

function writeLocal<T>(key: string, value: T): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export function getDemoProfile(): SessionProfile | null {
  return readLocal<SessionProfile | null>(PROFILE_KEY, null);
}

export function setDemoProfile(role: AccountRole, email: string): SessionProfile {
  const profile: SessionProfile = {
    id: `demo-${role}`,
    email,
    displayName: role === 'owner' ? 'Alex Property Owner' : role === 'contractor' ? 'RAD Field Team' : role === 'agent' ? 'Morgan Real Estate' : 'Growth Rings Operations',
    role,
    organizationName: role === 'contractor' ? 'RAD Design + Build' : role === 'agent' ? 'Mad River Property Group' : undefined
  };
  writeLocal(PROFILE_KEY, profile);
  return profile;
}

export function clearDemoProfile(): void {
  localStorage.removeItem(PROFILE_KEY);
}

export async function getLiveProfile(): Promise<SessionProfile | null> {
  if (!supabase) return getDemoProfile();
  const { data: authData } = await supabase.auth.getUser();
  if (!authData.user) return null;
  const { data, error } = await supabase.from('profiles').select('*').eq('id', authData.user.id).maybeSingle();
  if (error) throw error;
  if (!data?.primary_role) return null;
  return {
    id: data.id,
    email: data.email ?? authData.user.email ?? '',
    displayName: data.display_name ?? authData.user.email?.split('@')[0] ?? 'Growth Rings member',
    role: data.primary_role,
    organizationName: data.organization_name ?? undefined
  };
}

export async function saveLiveProfile(role: AccountRole, email: string): Promise<SessionProfile> {
  if (!supabase) return setDemoProfile(role, email);
  if (role === 'operator') throw new Error('Operator access must be granted administratively.');
  const { data: authData, error: authError } = await supabase.auth.getUser();
  if (authError || !authData.user) throw authError ?? new Error('Please sign in first.');
  const displayName = email.split('@')[0];
  const { data, error } = await supabase.from('profiles').upsert({
    id: authData.user.id,
    email,
    display_name: displayName,
    primary_role: role
  }).select('*').single();
  if (error) throw error;
  return {
    id: data.id,
    email: data.email,
    displayName: data.display_name,
    role: data.primary_role,
    organizationName: data.organization_name ?? undefined
  };
}

export async function listProperties(): Promise<PropertyRecord[]> {
  if (!supabase) return readLocal(PROPERTY_KEY, demoProperties);
  const { data, error } = await supabase.from('properties').select('*').order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id,
    name: row.name,
    address: row.address ?? '',
    municipality: row.municipality ?? '',
    region: row.region ?? '',
    coordinates: row.latitude && row.longitude ? { latitude: row.latitude, longitude: row.longitude } : undefined,
    parcelId: row.parcel_id ?? undefined,
    stage: row.stage,
    readiness: row.readiness ?? 0,
    openDecisions: row.open_decisions ?? 0,
    activeProjectCount: row.active_project_count ?? 0,
    updatedAt: row.updated_at
  }));
}

export async function createProperty(input: Omit<PropertyRecord, 'id' | 'readiness' | 'openDecisions' | 'activeProjectCount' | 'updatedAt'>): Promise<PropertyRecord> {
  if (!supabase) {
    const property: PropertyRecord = {
      ...input,
      id: crypto.randomUUID(),
      readiness: input.coordinates ? 36 : 22,
      openDecisions: 1,
      activeProjectCount: 0,
      updatedAt: new Date().toISOString()
    };
    const next = [property, ...readLocal(PROPERTY_KEY, demoProperties)];
    writeLocal(PROPERTY_KEY, next);
    return property;
  }

  const { data, error } = await supabase.rpc('create_property_with_owner', {
    p_name: input.name,
    p_address: input.address,
    p_municipality: input.municipality,
    p_region: input.region,
    p_stage: input.stage,
    p_latitude: input.coordinates?.latitude ?? null,
    p_longitude: input.coordinates?.longitude ?? null
  });
  if (error) throw error;
  const property = data as PropertyRecord & { eventId?: string };
  if (property.eventId) {
    const dispatch = await supabase.functions.invoke('dispatch-event', { body: { eventId: property.eventId } });
    if (dispatch.error) console.warn('Property saved; enrichment dispatch is awaiting retry.', dispatch.error);
  }
  return property;
}

export async function listLeads(): Promise<LeadRecord[]> {
  if (!supabase) return readLocal(LEAD_KEY, demoLeads);
  const { data, error } = await supabase.from('leads').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id,
    propertyId: row.property_id,
    projectId: row.project_id,
    title: row.title,
    location: row.location,
    trade: row.trade,
    fitScore: row.fit_score,
    status: row.status,
    clientPermission: row.client_permission,
    createdAt: row.created_at
  }));
}

export async function updateLeadStatus(id: string, status: LeadRecord['status']): Promise<void> {
  if (!supabase) {
    const next = readLocal(LEAD_KEY, demoLeads).map((lead) => lead.id === id ? { ...lead, status } : lead);
    writeLocal(LEAD_KEY, next);
    return;
  }
  const { error } = await supabase.from('leads').update({ status }).eq('id', id);
  if (error) throw error;
}

export async function createProjectLead(input: {
  propertyId: string;
  title: string;
  description: string;
  trade: string;
  timeline: string;
  budgetBand: string;
  clientPermission: boolean;
}): Promise<LeadRecord> {
  if (!supabase) {
    const property = readLocal(PROPERTY_KEY, demoProperties).find((item) => item.id === input.propertyId);
    const lead: LeadRecord = {
      id: crypto.randomUUID(), propertyId: input.propertyId, projectId: crypto.randomUUID(),
      title: input.title, location: property?.address || 'Location pending', trade: input.trade,
      fitScore: input.clientPermission ? 72 : 52, status: 'qualified',
      clientPermission: input.clientPermission, createdAt: new Date().toISOString()
    };
    writeLocal(LEAD_KEY, [lead, ...readLocal(LEAD_KEY, demoLeads)]);
    return lead;
  }
  const { data, error } = await supabase.rpc('create_project_lead', {
    p_property_id: input.propertyId,
    p_title: input.title,
    p_description: input.description,
    p_trade: input.trade,
    p_timeline: input.timeline,
    p_budget_band: input.budgetBand,
    p_client_permission: input.clientPermission
  });
  if (error) throw error;
  const result = data as { lead: LeadRecord; eventId: string };
  const dispatch = await supabase.functions.invoke('dispatch-event', { body: { eventId: result.eventId } });
  if (dispatch.error) console.warn('Lead saved; automation dispatch is awaiting retry.', dispatch.error);
  return result.lead;
}

export async function getMembership(propertyId?: string): Promise<MembershipRecord> {
  if (!supabase) return {
    planCode: 'pilot', status: 'active', propertyId, cancelAtPeriodEnd: false
  };
  const { data, error } = await supabase.rpc('get_my_membership', {
    target_property: propertyId ?? null
  });
  if (error) throw error;
  return data as MembershipRecord;
}

export async function startMembershipCheckout(planCode: Exclude<PlanCode, 'pilot'>, interval: BillingInterval, propertyId?: string): Promise<void> {
  if (!supabase) throw new Error('Connect Supabase before opening Stripe Checkout.');
  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body: { planCode, interval, propertyId: propertyId ?? null }
  });
  if (error) throw error;
  const url = (data as { url?: string })?.url;
  if (!url) throw new Error('Stripe Checkout did not return a destination.');
  window.location.assign(url);
}

export async function openBillingPortal(): Promise<void> {
  if (!supabase) throw new Error('Connect Supabase before opening billing.');
  const { data, error } = await supabase.functions.invoke('create-billing-portal', { body: {} });
  if (error) throw error;
  const url = (data as { url?: string })?.url;
  if (!url) throw new Error('The billing portal did not return a destination.');
  window.location.assign(url);
}
